import { useState, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { faqData, videoData, FAQItem, FAQCategory } from "@/shared/data/faqData";
import { renderFAQText } from "@/shared/utils/faqFormatter";
import styles from "./FAQ.module.css";

interface AccordionProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  category?: string;
}

function Accordion({ item, isOpen, onToggle, category }: AccordionProps) {
  const { darkMode } = useDarkMode();

  return (
    <div className={`${styles.accordionItem} ${darkMode ? styles.dark : ""}`}>
      <button className={styles.question} onClick={onToggle} aria-expanded={isOpen}>
        <span className={styles.questionText}>
          {item.question}
          {category && <span className={styles.categoryBadge}>{category}</span>}
        </span>
        <ChevronDown className={`${styles.chevron} ${isOpen ? styles.open : ""}`} />
      </button>
      {isOpen && <div className={styles.answer}>{renderFAQText(item.answer)}</div>}
    </div>
  );
}

function VideoCard({ title, description, youtubeId }: { title: string; description: string; youtubeId: string }) {
  const { darkMode } = useDarkMode();

  return (
    <div className={`${styles.videoCard} ${darkMode ? styles.dark : ""}`}>
      <div className={styles.videoEmbed}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&modestbranding=1`}
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          title={title}
        />
      </div>
      <p className={styles.videoCaption}>{description}</p>
    </div>
  );
}

export default function FAQView() {
  const { darkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState(faqData[0]?.id || "");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const results = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    const found: { item: FAQItem; category: FAQCategory }[] = [];
    faqData.forEach((cat) => {
      cat.items.forEach((item) => {
        const matchesQuestion = item.question.toLowerCase().includes(q);
        const matchesAnswer = item.answer.toLowerCase().includes(q);
        const matchesTags = item.tags?.some((tag) => tag.toLowerCase().includes(q));
        if (matchesQuestion || matchesAnswer || matchesTags) {
          found.push({ item, category: cat });
        }
      });
    });
    return found;
  }, [search]);

  const activeCategory = faqData.find((c) => c.id === activeTab);

  return (
    <div className={`${styles.wrapper} ${darkMode ? styles.dark : ""}`}>
      <div className={styles.header}>
        <h1 className={styles.title}>How To Use Hourglass</h1>
        <p className={styles.subtitle}>
          Everything you need to understand how Hourglass tracks time and creates time entries
        </p>
      </div>

      <div className={styles.navRow}>
        <div className={styles.tabs}>
          {faqData.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.tab} ${activeTab === cat.id ? styles.active : ""}`}
              onClick={() => { setActiveTab(cat.id); setSearch(""); }}
            >
              {cat.name}
            </button>
          ))}
        </div>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search FAQs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          <Search className={styles.searchIcon} size={18} />
        </div>
      </div>

      <div className={styles.content}>
        {results ? (
          <>
            <p className={styles.resultsLabel}>
              {results.length} result{results.length !== 1 ? "s" : ""} for "{search}"
            </p>
            {results.length > 0 ? (
              results.map(({ item, category }) => (
                <Accordion
                  key={item.id}
                  item={item}
                  isOpen={open.has(item.id)}
                  onToggle={() => toggle(item.id)}
                  category={category.name}
                />
              ))
            ) : (
              <div className={styles.noResults}>
                <Search size={48} />
                <p>No matching FAQs found</p>
                <span>Try different keywords or browse the categories</span>
              </div>
            )}
          </>
        ) : activeTab === "videos" ? (
          <div className={styles.videoGrid}>
            {videoData.map((video) => (
              <VideoCard
                key={video.id}
                title={video.title}
                description={video.description}
                youtubeId={video.youtubeId}
              />
            ))}
          </div>
        ) : (
          activeCategory?.items.map((item) => (
            <Accordion
              key={item.id}
              item={item}
              isOpen={open.has(item.id)}
              onToggle={() => toggle(item.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
